import { useQuery } from "@apollo/client";
import { FC } from "react";
import styled from "styled-components";

import { GAME_STATUS } from "enefel/game";
import { GET_GAMES } from "../../../query/GameQueries";
import { GET_TEAMS } from "../../../query/TeamQueries";
import QueryError from "../../Error/QueryError";
import Loading from "../../Loading";

const AnalyticsContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const AnalyticsTitle = styled.h1`
  color: #333;
  margin-bottom: 2rem;
  font-size: 2rem;
  text-align: center;
`;

const AnalyticsDescription = styled.div`
  background: #e3f2fd;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  font-size: 1rem;
  line-height: 1.6;
  border-left: 4px solid #2196f3;
`;

const SectionTitle = styled.h2`
  color: #333;
  margin: 3rem 0 1.5rem 0;
  font-size: 1.5rem;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 0.5rem;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  padding: 1.5rem;
  background: #f5f5f5;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #2196f3;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #666;
  font-weight: 500;
`;

const StatExplanation = styled.div`
  font-size: 0.8rem;
  color: #999;
  margin-top: 0.5rem;
`;

const InfoBox = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #856404;
`;

type SimpleGameAnalyticsProps = {};

export const SimpleGameAnalytics: FC<SimpleGameAnalyticsProps> = () => {
  // Requêtes simplifiées avec des limites plus larges
  const {
    data: gamesData,
    loading: gamesLoading,
    error: gamesError,
  } = useQuery(GET_GAMES, {
    variables: {
      leagueIds: null, // Toutes les ligues
      statuses: null, // Tous les statuts
      limit: 200, // Plus de parties
    },
  });

  const {
    data: teamsData,
    loading: teamsLoading,
    error: teamsError,
  } = useQuery(GET_TEAMS);

  // Gestion des états de chargement et d'erreur
  if (gamesLoading || teamsLoading) {
    return <Loading />;
  }

  if (gamesError || teamsError) {
    return <QueryError error={gamesError || teamsError} />;
  }

  const games = gamesData?.games || [];
  const teams = teamsData?.teams || [];

  // Informations de debug avec les vrais statuts
  const allGames = games.length;
  const waitingGames = games.filter(
    (g) => g.status === GAME_STATUS.WAITING
  ).length;
  const beforeGames = games.filter(
    (g) => g.status === GAME_STATUS.BEFORE
  ).length;
  const ongoingGames = games.filter(
    (g) => g.status === GAME_STATUS.ONGOING
  ).length;
  const tdGames = games.filter((g) => g.status === GAME_STATUS.TD).length;
  const endGames = games.filter((g) => g.status === GAME_STATUS.END).length;
  const finishedGames = games.filter(
    (g) => g.status === GAME_STATUS.FINISH
  ).length;
  const halftimeGames = games.filter(
    (g) => g.status === GAME_STATUS.HALFTIME
  ).length;

  // Parties vraiment terminées (END ou FINISH)
  const trulyFinishedGames = endGames + finishedGames;

  // Calculs simples des statistiques
  const totalGames = trulyFinishedGames;
  const totalTeams = teams.length;
  const averageScore =
    totalGames > 0
      ? Math.round(
          (games
            .filter(
              (g) =>
                g.status === GAME_STATUS.END || g.status === GAME_STATUS.FINISH
            )
            .reduce(
              (sum: number, game) =>
                sum + (game.score1 || 0) + (game.score2 || 0),
              0
            ) /
            totalGames) *
            10
        ) / 10
      : 0;

  const gameTypes = games
    .filter(
      (g) => g.status === GAME_STATUS.END || g.status === GAME_STATUS.FINISH
    )
    .reduce((acc, game) => {
      acc[game.type] = (acc[game.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const mostPopularType =
    Object.entries(gameTypes).sort(
      (a, b) => (b[1] as number) - (a[1] as number)
    )[0]?.[0] || "Aucun";

  return (
    <AnalyticsContainer>
      <AnalyticsTitle>📊 Analytics du Jeu Enefel - Debug</AnalyticsTitle>

      <AnalyticsDescription>
        <strong>Bienvenue dans le tableau de bord analytique d'Enefel !</strong>
        <br />
        Version debug avec les vrais statuts du jeu.
        <br />
        <em>Données basées sur les 200 dernières parties (tous statuts).</em>
      </AnalyticsDescription>

      <InfoBox>
        <strong>🔍 Mode Debug :</strong> Affichage de toutes les données
        disponibles avec les vrais statuts du jeu.
      </InfoBox>

      <SectionTitle>📈 Répartition par Statut (Vrais Statuts)</SectionTitle>
      <StatsContainer>
        <StatCard>
          <StatValue>{allGames}</StatValue>
          <StatLabel>Total Parties</StatLabel>
          <StatExplanation>Tous statuts confondus</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{waitingGames}</StatValue>
          <StatLabel>En Attente</StatLabel>
          <StatExplanation>WAITING (0)</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{beforeGames}</StatValue>
          <StatLabel>Avant Match</StatLabel>
          <StatExplanation>BEFORE (1)</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{ongoingGames}</StatValue>
          <StatLabel>En Cours</StatLabel>
          <StatExplanation>ONGOING (2)</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{tdGames}</StatValue>
          <StatLabel>Touchdown</StatLabel>
          <StatExplanation>TD (3)</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{endGames}</StatValue>
          <StatLabel>Fin</StatLabel>
          <StatExplanation>END (4)</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{finishedGames}</StatValue>
          <StatLabel>Terminé</StatLabel>
          <StatExplanation>FINISH (5)</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{halftimeGames}</StatValue>
          <StatLabel>Mi-temps</StatLabel>
          <StatExplanation>HALFTIME (6)</StatExplanation>
        </StatCard>
      </StatsContainer>

      <SectionTitle>📊 Statistiques Générales</SectionTitle>
      <StatsContainer>
        <StatCard>
          <StatValue>{trulyFinishedGames}</StatValue>
          <StatLabel>Parties Terminées</StatLabel>
          <StatExplanation>END + FINISH</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{totalTeams}</StatValue>
          <StatLabel>Équipes Total</StatLabel>
          <StatExplanation>Toutes les équipes</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{averageScore}</StatValue>
          <StatLabel>Score Moyen</StatLabel>
          <StatExplanation>Touchdowns par partie</StatExplanation>
        </StatCard>
        <StatCard>
          <StatValue>{mostPopularType}</StatValue>
          <StatLabel>Type Populaire</StatLabel>
          <StatExplanation>Type de partie le plus joué</StatExplanation>
        </StatCard>
      </StatsContainer>

      <SectionTitle>🎮 Répartition par Type de Partie</SectionTitle>
      <StatsContainer>
        {Object.entries(gameTypes).map(([type, count]) => (
          <StatCard key={type}>
            <StatValue>{count}</StatValue>
            <StatLabel>{type}</StatLabel>
            <StatExplanation>Parties terminées</StatExplanation>
          </StatCard>
        ))}
      </StatsContainer>

      {Object.keys(gameTypes).length === 0 && (
        <InfoBox>
          <strong>⚠️ Aucun type de partie trouvé :</strong> Cela peut indiquer
          qu'il n'y a pas de parties terminées dans la base de données.
        </InfoBox>
      )}

      <SectionTitle>🔧 Détails des Parties</SectionTitle>
      <InfoBox>
        <strong>Détails des 5 premières parties :</strong>
        <br />
        {games.slice(0, 5).map((game, index) => (
          <div
            key={game.id}
            style={{ marginTop: "0.5rem", fontSize: "0.9rem" }}
          >
            <strong>Partie {index + 1}:</strong> ID: {game.id}, Status:{" "}
            {game.status} ({getStatusName(game.status)}), Type: {game.type},
            Score: {game.score1}-{game.score2}
          </div>
        ))}
      </InfoBox>

      <AnalyticsDescription
        style={{
          marginTop: "3rem",
          background: "#f3e5f5",
          borderLeftColor: "#9c27b0",
        }}
      >
        <strong>💡 Diagnostic :</strong>
        <br />• <strong>Si peu de données</strong> : Vérifiez qu'il y a des
        parties dans la base
        <br />• <strong>Si pas de parties terminées</strong> : Les parties sont
        peut-être en cours
        <br />• <strong>Si pas d'équipes</strong> : Vérifiez la création
        d'équipes
        <br />• <strong>Prochaines étapes</strong> : Optimiser les requêtes une
        fois les données confirmées
      </AnalyticsDescription>
    </AnalyticsContainer>
  );
};

// Fonction helper pour afficher le nom du statut
const getStatusName = (status: number): string => {
  switch (status) {
    case GAME_STATUS.WAITING:
      return "WAITING";
    case GAME_STATUS.BEFORE:
      return "BEFORE";
    case GAME_STATUS.ONGOING:
      return "ONGOING";
    case GAME_STATUS.TD:
      return "TD";
    case GAME_STATUS.END:
      return "END";
    case GAME_STATUS.FINISH:
      return "FINISH";
    case GAME_STATUS.HALFTIME:
      return "HALFTIME";
    default:
      return "UNKNOWN";
  }
};
